import React, {
  useState,
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";

const CustomImageSlider = forwardRef(
  ({ items, onSlideChange, autoSlide = false, slideInterval = 2000 }, ref) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [imageRatios, setImageRatios] = useState({});
    const [loadedSrcs, setLoadedSrcs] = useState({});
    const sliderRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const [startX, setStartX] = useState(0);
    const [currentTranslate, setCurrentTranslate] = useState(0);
    const [previousTranslate, setPreviousTranslate] = useState(0);
    const [animationId, setAnimationId] = useState(null);

    useImperativeHandle(ref, () => ({
      slideToIndex: (index) => {
        if (index >= 0 && index < items.length) {
          setCurrentIndex(index);
        }
      },
      getCurrentIndex: () => currentIndex,
    }));

    useEffect(() => {
      if (onSlideChange) {
        onSlideChange(currentIndex);
      }
    }, [currentIndex, onSlideChange]);

    useEffect(() => {
      const timer = autoSlide
        ? setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % items.length);
          }, slideInterval)
        : null;

      return () => {
        if (timer) clearInterval(timer);
      };
    }, [autoSlide, slideInterval, items.length]);

    useEffect(() => {
      if (currentIndex >= items.length) {
        setCurrentIndex(Math.max(items.length - 1, 0));
      }
    }, [currentIndex, items.length]);

    const getImageSrc = (item) => {
      if (!item) return "";
      if (typeof item === "string") return item;
      return item.original || item.base64 || "";
    };

    const activeImageSrc = getImageSrc(items[currentIndex]);
    const activeRatio = useMemo(
      () => imageRatios[activeImageSrc] || null,
      [activeImageSrc, imageRatios]
    );

    const handleImageLoad = (event, src) => {
      const { naturalWidth, naturalHeight } = event.currentTarget;
      if (!naturalWidth || !naturalHeight || !src) return;

      const ratio = `${naturalWidth} / ${naturalHeight}`;
      setImageRatios((prev) => (
        prev[src] === ratio ? prev : { ...prev, [src]: ratio }
      ));
      setLoadedSrcs((prev) => (prev[src] ? prev : { ...prev, [src]: true }));
    };

    useEffect(() => {
      if (!sliderRef.current) return;

      const containerWidth = sliderRef.current.clientWidth;
      setCurrentTranslate(-currentIndex * containerWidth);
      setPreviousTranslate(-currentIndex * containerWidth);
    }, [currentIndex, activeRatio]);

    const start = (event) => {
      setIsDragging(true);
      setStartX(getPositionX(event));
      setAnimationId(requestAnimationFrame(animation));
    };

    const end = () => {
      setIsDragging(false);
      cancelAnimationFrame(animationId);

      const movedBy = currentTranslate - previousTranslate;
      if (movedBy < -100) {
        setCurrentIndex((prevIndex) =>
          Math.min(prevIndex + 1, items.length - 1)
        );
      }
      if (movedBy > 100) {
        setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      }

      const containerWidth = sliderRef.current.clientWidth;
      setCurrentTranslate(-currentIndex * containerWidth);
      setPreviousTranslate(-currentIndex * containerWidth);
    };

    const move = (event) => {
      if (isDragging) {
        const currentPosition = getPositionX(event);
        const diff = currentPosition - startX;
        setCurrentTranslate(previousTranslate + diff);
      }
    };

    const getPositionX = (event) => {
      return event.type.includes("mouse")
        ? event.pageX
        : event.touches[0].clientX;
    };

    const animation = () => {
      setSliderPosition();
      if (isDragging) setAnimationId(requestAnimationFrame(animation));
    };

    const setSliderPosition = () => {
      sliderRef.current.style.transform = `translateX(${currentTranslate}px)`;
    };

    return (
        <div
          className="image-slider-frame relative w-full overflow-hidden rounded-[var(--inyc-input-border-radius)]"
          style={activeRatio ? { aspectRatio: activeRatio } : undefined}
          onMouseDown={start}
          onMouseUp={end}
          onMouseLeave={end}
          onMouseMove={move}
          onTouchStart={start}
          onTouchEnd={end}
          onTouchMove={move}
        >
          <div
            ref={sliderRef}
            className={`flex h-full max-h-full ${isDragging ? "" : "duration-300"}`}
            style={{ transform: `translateX(${currentTranslate}px)` }}
          >
            {items.map((item, index) => {
              const src = getImageSrc(item);
              const distanceFromCurrent = Math.abs(currentIndex - index);
              const scale = 1 - 0.1 * distanceFromCurrent;
              const opacity = 1 - 0.2 * distanceFromCurrent;
              const zIndex = 1000 - distanceFromCurrent; // Ensure current image is always on top

              return (
                <div
                  style={{
                    transform: `scale(${scale})`,
                    opacity: opacity,
                    zIndex: zIndex,
                  }}
                  key={index}
                  className="min-w-full flex justify-center items-center max-h-full h-full duration-300"
                >
                  <img
                    src={src}
                    alt={`Slide ${index}`}
                    className={`image-slider-img rounded-[var(--inyc-input-border-radius)] h-full w-full object-contain ${loadedSrcs[src] ? "is-loaded" : ""}`}
                    onLoad={(event) => handleImageLoad(event, src)}
                    ref={(img) => {
                      if (img?.complete && img.naturalWidth) {
                        queueMicrotask(() => handleImageLoad({ currentTarget: img }, src));
                      }
                    }}
                  />
                </div>
              );
            })}
          </div>
          {items?.length > 1 && (
            <>
              <div
                className="cursor-pointer absolute left-[0.5rem] top-[50%] translate-y-[-50%] z-[1001] bg-white/70 flex items-center justify-center text-kit-xs w-[2.5rem] h-[2.5rem] rounded-kit-full duration-300 hover:bg-white"
                onClick={() => {
                  if (currentIndex === 0) {
                    setCurrentIndex(items.length - 1);
                  } else {
                    setCurrentIndex((prevIndex) => Math.max(prevIndex - 1, 0));
                  }
                }}
              >
                &#10094;
              </div>
              <div
                className="cursor-pointer absolute right-[0.5rem] top-[50%] translate-y-[-50%] z-[1001] bg-white/70 flex items-center justify-center text-kit-xs w-[2.5rem] h-[2.5rem] rounded-kit-full duration-300 hover:bg-white"
                onClick={() => {
                  if (currentIndex === items.length - 1) {
                    setCurrentIndex(0);
                  } else {
                    setCurrentIndex((prevIndex) =>
                      Math.min(prevIndex + 1, items.length - 1)
                    );
                  }
                }}
              >
                &#10095;
              </div>
              <div className="image-slider-dots absolute bottom-[1.5rem] left-0 w-full flex justify-center gap-kit-8">
                {items.map((img, i) => (
                  <div
                    key={i}
                    className={`h-[0.6rem] duration-300 rounded-kit-full cursor-pointer ${
                      i === currentIndex
                        ? "bg-white w-[1.5rem]"
                        : "w-[0.6rem] bg-white/70"
                    }`}
                    onClick={() => {
                      setCurrentIndex(i);
                    }}
                  ></div>
                ))}
              </div>
            </>
          )}
        </div>
    );
  }
);

export default CustomImageSlider;
